/* Styles for the shared <FindingsBlock> renderer (a ```netwatch-findings
 * block inside an assistant answer). Hosts load this file: the dashboard
 * imports `@netwatchai/chat-core/findings.css`; the widget inlines it into
 * its shadow-root <style>.
 *
 * Colours read the host's `--nw-*` chat-content tokens (the widget sets them
 * per theme in its shadow DOM, the SPA maps them onto its theme tokens) and
 * fall back to the dark chat-content values. The few status colours the
 * tokens don't carry are defined on `.nw-fb` with a `.nw-theme-light`
 * override, the same convention as outlook.css / incidents.css. */

.nw-fb {
  --fb-green: #46b27e;
  --fb-amber: #d99a3a;
  --fb-red: #d9605c;
  --fb-warn-bg: rgba(217, 96, 92, 0.10);
  --fb-warn-line: rgba(217, 96, 92, 0.35);
  --fb-ok-bg: rgba(70, 178, 126, 0.12);
  --fb-ok-line: rgba(70, 178, 126, 0.35);
  --fb-amber-bg: rgba(217, 154, 58, 0.14);
  --fb-amber-line: rgba(217, 154, 58, 0.45);
  --fb-thr: rgba(217, 96, 92, 0.45);

  margin: 10px 0;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  border-radius: 12px;
  background: var(--nw-bg, #141b25);
  color: var(--nw-text, #e8edf2);
  font-size: 13px;
  line-height: 1.45;
  overflow: hidden;
  /* The strip reflows on the BLOCK's width, not the viewport's: the widget
     panel is a few hundred px wide on a full-width host page. */
  container-type: inline-size;
}
.nw-theme-light .nw-fb {
  --fb-green: #1f8f5a;
  --fb-amber: #b7791f;
  --fb-red: #c8443f;
  --fb-warn-bg: rgba(200, 68, 63, 0.07);
  --fb-warn-line: rgba(200, 68, 63, 0.30);
  --fb-ok-bg: rgba(31, 143, 90, 0.10);
  --fb-ok-line: rgba(31, 143, 90, 0.35);
  --fb-amber-bg: rgba(183, 121, 31, 0.10);
  --fb-amber-line: rgba(183, 121, 31, 0.40);
  --fb-thr: rgba(200, 68, 63, 0.40);
}

/* --- verdict line ------------------------------------------------------- */
.nw-fb-verdict {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px 11px;
  border-bottom: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
}
.nw-fb-verdict .nw-sev-icon { margin-top: 1px; }
.nw-fb-verdict-text { flex: 1; min-width: 0; }
.nw-fb-verdict-title { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
.nw-fb-verdict-detail {
  margin: 3px 0 0;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
.nw-fb-caption {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
}
.nw-fb-state {
  margin-left: 8px;
  vertical-align: middle;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
}
.nw-fb-state-confirmed { color: var(--fb-green); background: var(--fb-ok-bg); border-color: var(--fb-ok-line); }
.nw-fb-state-probable { color: var(--fb-amber); background: var(--fb-amber-bg); border-color: var(--fb-amber-line); }
.nw-fb-state-inconclusive { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); border-color: var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-lead {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  font-variant-numeric: tabular-nums;
}
.nw-fb-checked {
  flex: none;
  text-align: right;
  padding-left: 8px;
  border-left: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.nw-fb-checked-n { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.1; }
.nw-fb-checked-l { font-size: 11px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }

/* --- sections ----------------------------------------------------------- */
.nw-fb-sec {
  padding: 12px 14px;
  border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
}
.nw-fb-sec:first-of-type { border-top: none; }
.nw-fb-h { margin: 0 0 3px; font-size: 13px; font-weight: 700; }
.nw-fb-p { margin: 0 0 8px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-p:last-child { margin-bottom: 0; }
.nw-fb-sub { margin-bottom: 8px; }
.nw-fb-look .nw-fb-p { color: var(--nw-text, #e8edf2); }

/* --- bullet-line sections (recurring / processes / problems / next / footer) */
.nw-fb-lines {
  margin: 0; padding-left: 18px;
  display: flex; flex-direction: column; gap: 4px;
}
.nw-fb-lines li { font-size: 12.5px; line-height: 1.45; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-lines .nw-fb-lines { margin-top: 3px; gap: 2px; }
.nw-fb-lines .nw-fb-lines li { font-size: 11.5px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-lines code { font-family: var(--font-mono, ui-monospace, monospace); font-size: 11.5px; }
.nw-fb-lines-more { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
/* The model's layer (remediation, caveats) — demarcated from the
   deterministic sections above it. */
.nw-fb-footer { border-top-style: dashed; }
.nw-fb-footer .nw-fb-h { margin-top: 10px; }
.nw-fb-footer .nw-fb-h:first-child { margin-top: 0; }
.nw-fb-h-warn { color: var(--nw-warning, #e2b93b); }

/* --- pattern strip -------------------------------------------------------- */
.nw-fb-banner {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04));
  margin-bottom: 10px;
}
.nw-fb-banner-warn { background: var(--fb-warn-bg); border-color: var(--fb-warn-line); }
.nw-fb-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.nw-fb-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 4px 5px;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  border-radius: 8px;
  min-width: 0;
}
.nw-fb-day-spike { border-color: var(--fb-warn-line); background: var(--fb-warn-bg); }
.nw-fb-day-clean { background: var(--fb-ok-bg); border-color: var(--fb-ok-line); }
.nw-fb-day-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.nw-fb-day-body {
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.nw-fb-spark { display: block; max-width: 100%; height: 26px; }
.nw-fb-spark-line { fill: none; stroke: var(--fb-red); stroke-width: 1.5; stroke-linejoin: round; stroke-linecap: round; }
.nw-fb-spark-thr { stroke: var(--fb-thr); stroke-width: 1; stroke-dasharray: 2 2; }
.nw-fb-day-peak { font-size: 11px; font-weight: 600; color: var(--fb-red); font-variant-numeric: tabular-nums; }
.nw-fb-day-none { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-day-cap {
  font-size: 10.5px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.nw-fb-day-spike .nw-fb-day-cap { color: var(--fb-red); font-weight: 600; }

/* A narrow widget panel: two rows of cards rather than seven slivers. */
@container (max-width: 480px) {
  .nw-fb-days { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

/* --- movers --------------------------------------------------------------- */
.nw-fb-mover {
  margin-top: 8px;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  border-radius: 8px;
  overflow: hidden;
}
.nw-fb-mover-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04));
}
.nw-fb-mover-label { font-weight: 600; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nw-fb-mover-val { font-variant-numeric: tabular-nums; white-space: nowrap; }
.nw-fb-mover-base { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-mover-corr {
  font-variant-numeric: tabular-nums;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
  font-size: 12px;
  white-space: nowrap;
}
.nw-fb-mover-chart { padding: 8px 8px 4px; }
.nw-fb-mover-in_band .nw-fb-mover-row { background: transparent; }

/* --- pills ---------------------------------------------------------------- */
.nw-fb-pill {
  flex: none;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
  text-transform: lowercase;
}
.nw-fb-pill-normal { color: var(--fb-green); background: var(--fb-ok-bg); border-color: var(--fb-ok-line); }
.nw-fb-pill-warn { color: var(--fb-amber); background: var(--fb-amber-bg); border-color: var(--fb-amber-line); }
.nw-fb-pill-strong { color: var(--fb-red); background: var(--fb-warn-bg); border-color: var(--fb-warn-line); }
.nw-fb-pill-inband { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); border-color: var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-check-tag {
  margin-left: 6px;
  font-size: 11px;
  color: var(--fb-amber);
}

/* --- slate rows ------------------------------------------------------- */
.nw-fb-slate-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 7px 10px;
  border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
}
.nw-fb-slate-row:first-child { border-top: 0; }
.nw-fb-slate-hyp { flex: 1; min-width: 0; }
.nw-fb-slate-detail {
  flex-basis: 100%;
  font-size: 12px;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
.nw-fb-slate-row { flex-wrap: wrap; }
.nw-fb-pill-muted { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); border-color: var(--nw-border, rgba(255, 255, 255, 0.12)); }

/* --- normal groups -------------------------------------------------------- */
.nw-fb-groups { margin-top: 4px; }
.nw-fb-group { border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-group:first-child { border-top: none; }
.nw-fb-group-row {
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 4px;
  cursor: pointer;
  color: inherit;
  font: inherit;
}
.nw-fb-group-row:hover { background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04)); }
.nw-fb-group-row:focus-visible { outline: 2px solid var(--nw-accent, #5b93e8); outline-offset: -2px; border-radius: 6px; }
.nw-fb-group-caret {
  width: 12px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  font-size: 11px;
  transition: transform 120ms ease;
}
.nw-fb-group.open .nw-fb-group-caret { transform: rotate(90deg); }
.nw-fb-group-label { flex: 1; font-weight: 600; }
.nw-fb-group-count { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-size: 12px; white-space: nowrap; }
.nw-fb-checks {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 8px;
  font-size: 12.5px;
}
.nw-fb-checks td {
  padding: 4px 6px 4px 24px;
  border-top: 1px dashed var(--nw-border, rgba(255, 255, 255, 0.12));
  vertical-align: middle;
}
.nw-fb-checks td + td { padding-left: 6px; }
.nw-fb-check-label { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-check-val {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
/* Column heads appear only when the rows carry spike-window ranges. */
.nw-fb-checks-head th {
  padding: 6px 6px 2px 24px;
  font-size: 11px;
  font-weight: 500;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  text-align: right;
  white-space: nowrap;
}
.nw-fb-checks-head th + th { padding-left: 6px; }
.nw-fb-gaps .nw-fb-group-label { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-gaps-list {
  padding: 2px 4px 10px 24px;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  font-size: 12px;
}

/* --- fallbacks / loading -------------------------------------------------- */
.nw-fb-note { padding: 10px 14px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-rawwrap { padding: 0 14px 10px; }
.nw-fb-rawwrap summary { cursor: pointer; font-size: 12px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-raw {
  margin: 6px 0 0;
  max-height: 200px;
  overflow: auto;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
}
.nw-fb-skel { padding: 14px; }
.nw-fb-skel-bar {
  height: 12px;
  border-radius: 6px;
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.06));
  margin-bottom: 8px;
  animation: nw-fb-pulse 1.2s ease-in-out infinite;
}
.nw-fb-skel-bar.short { width: 55%; margin-bottom: 0; }
@keyframes nw-fb-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }

/* Processes measured across the spike windows: name · min–max · windows.
   Same table as the groups, without the group caret indent. */
.nw-fb-procs td, .nw-fb-procs .nw-fb-checks-head th { padding-left: 6px; }
.nw-fb-proc-name {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 12px;
  word-break: break-all;
}
/* A row that leads with a client-readable name is prose, not a process
   string — the mono face belongs to the measured name beside it. */
.nw-fb-proc-name.is-plain {
  font-family: inherit;
  font-size: 12.5px;
  word-break: normal;
}
.nw-fb-proc-win {
  text-align: right;
  white-space: nowrap;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
}
.nw-fb-note-small { margin: 0 0 8px; font-size: 11.5px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }

/* --- occurrence slots ------------------------------------------------------
   The `seen in` cell is a miniature of the strip above it: one slot per
   occurrence, same order, filled where the process was listed — so two
   processes that ran in the same spikes line up down the column before
   anything is opened. A slot the snapshot history no longer covers stays in
   place, dashed: it is still an occurrence, just not one we can answer for.
   The count is the control; opening it names the occurrences. */
.nw-fb-occ-btn {
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  /* Hugs its content, pushed to the cell's right edge: a full-width button
     would wash the whole row on hover and read as a row action. */
  width: max-content;
  margin: -3px -6px -3px auto;
  padding: 3px 6px 3px 8px;
  border-radius: 6px;
  cursor: pointer;
  color: inherit;
  font: inherit;
}
.nw-fb-occ-btn:hover { background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04)); }
.nw-fb-occ-btn:hover .nw-fb-occ-n,
.nw-fb-proc-open .nw-fb-occ-n { color: var(--nw-text, #e8edf2); }
.nw-fb-occ-btn:focus-visible { outline: 2px solid var(--nw-accent, #5b93e8); outline-offset: -2px; }
.nw-fb-occ-slots { display: inline-flex; align-items: center; gap: 4px; }
.nw-fb-occ-slot {
  width: 9px;
  height: 8px;
  border-radius: 2px;
  flex: none;
  box-sizing: border-box;
  background: var(--nw-text, #e8edf2);
}
.nw-fb-occ-slot.is-unseen {
  background: transparent;
  box-shadow: inset 0 0 0 1px var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
}
/* Struck through, not empty: this occurrence happened, we just cannot answer
   for it — the one state the fraction alone would quietly round away. */
.nw-fb-occ-slot.is-no_data {
  background: linear-gradient(-45deg, transparent 43%, var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)) 43%,
              var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)) 57%, transparent 57%);
  box-shadow: inset 0 0 0 1px var(--nw-border, rgba(255, 255, 255, 0.12));
}
.nw-fb-occ-n { font-weight: 600; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-occ-caret {
  width: 8px;
  font-size: 10px;
  line-height: 1;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  transition: transform 120ms ease;
}
.nw-fb-proc-open .nw-fb-occ-caret { transform: rotate(90deg); }

/* The occurrences named, under the row they belong to (never a floating
   layer: this card lives in a scrolling stream and, in the widget, a shadow
   root). Each line repeats its slot, so the dots above are their own key. */
.nw-fb-proc-detailrow > td { padding: 0 6px 8px; border-top: none; }
.nw-fb-occ-list {
  list-style: none;
  margin: 0;
  padding: 6px 9px 7px;
  border-radius: 7px;
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04));
  display: grid;
  grid-template-columns: 9px max-content max-content 1fr;
  align-items: center;
  gap: 5px 9px;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
/* The rows share the list's columns (times under times), so each item hands
   its four cells straight to the grid. */
.nw-fb-occ-item { display: contents; }
.nw-fb-occ-day {
  font-weight: 600;
  color: var(--nw-text, #e8edf2);
  white-space: nowrap;
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
}
.nw-fb-occ-day small {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nw-accent, #5b93e8);
}
.nw-fb-occ-clock { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-occ-read { text-align: right; color: var(--nw-text, #e8edf2); white-space: nowrap; }
.nw-fb-occ-read em {
  font-style: normal;
  font-weight: 500;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
}
.nw-fb-occ-item.is-unseen > *, .nw-fb-occ-item.is-no_data > * { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-occ-item.is-unseen .nw-fb-occ-day,
.nw-fb-occ-item.is-no_data .nw-fb-occ-day {
  font-weight: 500;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
.nw-fb-occ-item.is-unseen .nw-fb-occ-read,
.nw-fb-occ-item.is-no_data .nw-fb-occ-read { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
@media (prefers-reduced-motion: reduce) {
  .nw-fb-occ-caret { transition: none; }
}
/* Narrow widget panel: the fraction and the caret carry the cell; the slots
   would crowd the process names, and the expansion says it all anyway. */
@container (max-width: 400px) {
  .nw-fb-occ-slots { display: none; }
  .nw-fb-occ-list { gap: 5px 7px; padding: 6px 7px 7px; }
}

/* --- the out-of-window ledger (docs/process-recurrence.md amendment 4) ---- */
/* Set apart from the grid above by a rule — these are different hours and the
   two must never read as one table — and then drawn as the SAME table, because
   it is the same measurement over a different set of hours. */
.nw-fb-procother {
  margin-top: 11px;
  padding-top: 10px;
  border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
}
/* The finding. The one line that has to land if nothing else is read. */
.nw-fb-procother-find {
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--nw-text, #e8edf2);
}
/* WHICH hours the finding is about — the answer to the question the old
   "all four of these hours" left the reader holding. Tabular figures so four
   clock times read as a list of times, not as prose. */
.nw-fb-procother-when {
  margin: 3px 0 0;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  color: var(--nw-text, #e8edf2);
}
.nw-fb-procother-sub {
  margin: 3px 0 0;
  font-size: 12.5px;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
/* The comparison, inside the finding's own paragraph: lifted to ink so it
   carries, without becoming a second heading. */
.nw-fb-procother-sub b {
  font-weight: 600;
  color: var(--nw-text, #e8edf2);
}
.nw-fb-procother-table { margin-top: 9px; }
/* The measured name, beside the readable one: an admin still finds the string
   they know, and it never competes with it for the row. */
.nw-fb-proc-tech {
  margin-left: 7px;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  font-family: var(--nw-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
}
/* Narrow widget panel: the row is the name and its count; the string an admin
   greps for is the half that goes. */
@container (max-width: 420px) {
  .nw-fb-proc-tech { display: none; }
}

/* --- producer tables (docs/findings-everywhere.md) ------------------------ */
/* A titled grid of formatted string cells — outages, hosts below SLA, drives
   filling, lockouts by caller. Same table as the groups/processes, with real
   column heads; wide grids scroll inside the card, never the page. */
.nw-fb-tablewrap { overflow-x: auto; max-width: 100%; }
.nw-fb-table td, .nw-fb-table .nw-fb-checks-head th { padding-left: 6px; }
.nw-fb-table td { white-space: nowrap; font-variant-numeric: tabular-nums; }
.nw-fb-table .nw-fb-cell-left, .nw-fb-table .nw-fb-checks-head th.nw-fb-cell-left { text-align: left; }
.nw-fb-table .nw-fb-cell-right, .nw-fb-table .nw-fb-checks-head th.nw-fb-cell-right { text-align: right; }
.nw-fb-table .nw-fb-cell-lead { color: var(--nw-text, #e8edf2); font-weight: 600; }
.nw-fb-row-warn .nw-fb-cell-lead { color: var(--fb-amber); }
.nw-fb-row-strong .nw-fb-cell-lead { color: var(--fb-red); }
.nw-fb-row-muted td { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-row-muted .nw-fb-cell-lead { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-weight: 500; }
.nw-fb-row-indent .nw-fb-cell-lead { padding-left: 22px; font-weight: 500; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-row-indent td { border-top-style: dotted; }

/* The red verdict: the Disaster octagon leads the line and the title takes
   its colour, so "Offline now" reads as red before the words do. */
.nw-fb-critical .nw-fb-verdict-title { color: var(--fb-red); }

/* --- pattern lanes (v2) ---------------------------------------------------
   One 24h lane per day, the asked window as a tinted column, solid marks for
   in-window spikes, hollow marks + a time label for spikes at other times.
   Tokens follow the file's convention: --nw-* from the host, the two window
   tints declared here with a light override. */
.nw-fb {
  --fb-win-bg: rgba(91, 147, 232, 0.12);
  --fb-win-line: rgba(91, 147, 232, 0.34);
}
.nw-theme-light .nw-fb {
  --fb-win-bg: rgba(47, 111, 208, 0.10);
  --fb-win-line: rgba(47, 111, 208, 0.32);
}
.nw-fb-lanes {
  --fb-lane-day-w: 56px;
  --fb-lane-cap-w: 128px;
  display: grid;
  /* The caption column grows to its longest line ("down 07:53 · 8 min ·
     +3 hosts" on an outage card) rather than clipping it; the lane gives
     the room up. */
  grid-template-columns: var(--fb-lane-day-w) minmax(0, 1fr) minmax(var(--fb-lane-cap-w), max-content);
  column-gap: 10px;
}
.nw-fb-lanes-axis { grid-column: 2; position: relative; height: 30px; margin-bottom: 2px; }
.nw-fb-lanes-t {
  position: absolute; bottom: 0; transform: translateX(-50%);
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.04em;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5));
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
.nw-fb-lanes-t.first { transform: none; }
.nw-fb-lanes-t.last { transform: translateX(-100%); }
.nw-fb-lanes-wintag {
  position: absolute; top: 0;
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em;
  color: var(--nw-accent, #5b93e8); white-space: nowrap;
  display: inline-flex; align-items: center; gap: 4px;
  font-variant-numeric: tabular-nums;
}
.nw-fb-lanes-wintag.right { transform: translateX(-100%); }
.nw-fb-lanes-wintag::before {
  content: ""; width: 8px; height: 8px; border-radius: 2px;
  background: var(--fb-win-bg); box-shadow: inset 0 0 0 1px var(--fb-win-line);
}
.nw-fb-lanes-head {
  grid-column: 3; height: 30px; margin-bottom: 2px;
  display: flex; align-items: flex-end; justify-content: flex-end;
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); white-space: nowrap;
}
.nw-fb-lane-day {
  display: flex; flex-direction: column; justify-content: center; height: 28px;
  font-size: 11.5px; font-weight: 600; line-height: 1.1;
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
  white-space: nowrap; font-variant-numeric: tabular-nums;
}
.nw-fb-lane-day.nw-fb-lane-today { color: var(--nw-text, #e8edf2); }
.nw-fb-lane-day small {
  font-size: 9px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--nw-accent, #5b93e8);
}
.nw-fb-lane { position: relative; height: 28px; }
.nw-fb-lane::before {
  content: ""; position: absolute; left: 0; right: 0; top: 5px; height: 18px;
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04)); border-radius: 4px;
}
.nw-fb-lane-grid { position: absolute; top: 5px; height: 18px; width: 1px; background: var(--nw-border, rgba(255, 255, 255, 0.12)); opacity: 0.55; }
.nw-fb-lane-win {
  position: absolute; top: 0; bottom: 0;
  background: var(--fb-win-bg);
  border-left: 1px solid var(--fb-win-line); border-right: 1px solid var(--fb-win-line);
}
.nw-fb-lane-mark {
  position: absolute; top: 50%; transform: translateY(-50%);
  height: 10px; min-width: 6px; border-radius: 3px;
  background: var(--fb-red);
  box-shadow: 0 0 0 2px var(--nw-bg, #141b25);
}
.nw-fb-lane-other {
  min-width: 12px; background: var(--nw-bg, #141b25);
  box-shadow: inset 0 0 0 1.5px var(--fb-red), 0 0 0 2px var(--nw-bg, #141b25);
}
.nw-fb-lane-rel {
  /* Never reached the alert line — amber, not red, and a thinner ring, so a
     glance can tell "high for this host" from "over the line". */
  box-shadow: inset 0 0 0 1.5px var(--fb-amber), 0 0 0 2px var(--nw-bg, #141b25);
}
.nw-fb-lane-markt {
  position: absolute; top: 50%; transform: translateY(-50%);
  font-size: 10.5px; font-weight: 700; color: var(--fb-red);
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
.nw-fb-lane-markt.left { transform: translate(-100%, -50%); }
.nw-fb-lane-markt-rel { color: var(--fb-amber); font-weight: 600; }
.nw-fb-lane-cap {
  display: flex; align-items: center; justify-content: flex-end; gap: 5px;
  height: 28px; font-size: 11.5px; font-weight: 600; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; font-variant-numeric: tabular-nums;
}
.nw-fb-lane-cap-spike { color: var(--fb-red); }
.nw-fb-lane-cap-clean { color: var(--fb-green); font-weight: 500; }
.nw-fb-lane-cap-no_data { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-weight: 500; }
.nw-fb-lane-dim { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); font-weight: 500; }
.nw-fb-lanes-tally {
  display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 10px; padding-top: 10px;
  border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  font-size: 12px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
.nw-fb-lanes-tally span { display: inline-flex; align-items: center; gap: 7px; }
.nw-fb-lanes-tally b { font-size: 13px; font-weight: 700; color: var(--nw-text, #e8edf2); font-variant-numeric: tabular-nums; }
.nw-fb-key { width: 12px; height: 8px; border-radius: 2px; background: var(--fb-red); flex: none; }
.nw-fb-key-other { background: transparent; box-shadow: inset 0 0 0 1.5px var(--fb-red); }
.nw-fb-key-ok { width: 12px; height: 12px; border-radius: 50%; background: var(--fb-ok-bg); box-shadow: inset 0 0 0 1px var(--fb-ok-line); }

/* A derived (recurring) window: the producer found it, the user did not ask
   for it — dashed edges, "recurring window" tag. */
.nw-fb-lane-win.derived { border-left-style: dashed; border-right-style: dashed; }
.nw-fb-lanes-wintag.derived::before { box-shadow: inset 0 0 0 1px var(--fb-win-line); border: 1px dashed var(--fb-win-line); background: transparent; box-sizing: border-box; }
.nw-fb-lanes-tally-note { color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }

/* --- mark tooltips -----------------------------------------------------------
   The caption is the scan layer; the tip is the precision layer (exact
   from–till, duration, peak, rider) so nobody guesses when a mark happened.
   Pure CSS from data-tip; the hit area is padded well past the 10px mark. */
.nw-fb { --fb-tip-shadow: 0 4px 14px rgba(0, 0, 0, 0.35); }
.nw-theme-light .nw-fb { --fb-tip-shadow: 0 4px 14px rgba(17, 24, 39, 0.16); }
.nw-fb-lane:hover { z-index: 2; }
.nw-fb-lane-mark[data-tip] { cursor: default; }
.nw-fb-lane-mark[data-tip]::before { content: ""; position: absolute; inset: -8px -7px; }
.nw-fb-lane-mark[data-tip]::after {
  content: attr(data-tip);
  position: absolute; bottom: calc(100% + 7px); left: 50%;
  transform: translate(-50%, 3px);
  width: max-content; max-width: 250px;
  padding: 5px 8px; border-radius: 6px;
  font-size: 11px; font-weight: 600; line-height: 1.45; text-align: left;
  font-variant-numeric: tabular-nums; white-space: normal;
  color: var(--nw-text, #e8edf2);
  background: var(--nw-bg, #141b25);
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  box-shadow: var(--fb-tip-shadow);
  opacity: 0; visibility: hidden; pointer-events: none;
  transition: opacity 0.12s ease-out, transform 0.12s ease-out, visibility 0.12s;
  z-index: 3;
}
.nw-fb-lane-mark[data-tip]:hover::after {
  opacity: 1; visibility: visible; transform: translate(-50%, 0); transition-delay: 60ms;
}
.nw-fb-lane-mark.tipleft[data-tip]::after { left: -4px; transform: translate(0, 3px); }
.nw-fb-lane-mark.tipleft[data-tip]:hover::after { transform: translate(0, 0); }
.nw-fb-lane-mark.tipright[data-tip]::after { left: auto; right: -4px; transform: translate(0, 3px); }
.nw-fb-lane-mark.tipright[data-tip]:hover::after { transform: translate(0, 0); }
@media (prefers-reduced-motion: reduce) {
  .nw-fb-lane-mark[data-tip]::after { transition: none; }
}

/* --- shaped tables (docs/findings-everywhere.md, "Shaped tables") ----------
   Bars, tiles and the score readout share three primitives: a meter (fill +
   floor tick + figure), a chip, and a track. The grid stays the fallback. */
.nw-fb {
  --fb-track: rgba(255, 255, 255, 0.09);
  --fb-yellow: #FFC859;
  --fb-orange: #FFA059;
}
.nw-theme-light .nw-fb {
  --fb-track: rgba(17, 24, 39, 0.10);
  --fb-yellow: #b7930f;
  --fb-orange: #c9712a;
}
.nw-fb-meter { display: inline-flex; align-items: center; gap: 7px; min-width: 0; }
.nw-fb-meter i {
  position: relative; width: 64px; height: 6px; border-radius: 3px;
  background: var(--fb-track); flex: none; display: block;
}
.nw-fb-meter i::before {
  content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: var(--fb-v, 0%);
  border-radius: 3px; background: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
}
.nw-fb-meter-warn i::before { background: var(--fb-amber); }
.nw-fb-meter-strong i::before { background: var(--fb-red); }
.nw-fb-meter i b {
  position: absolute; top: -3px; bottom: -3px; width: 1.5px; margin-left: -1px;
  background: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); opacity: 0.7;
}
.nw-fb-meter em { font-style: normal; font-size: 11.5px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
.nw-fb-meter-warn em { color: var(--fb-amber); }
.nw-fb-meter-strong em { color: var(--fb-red); }

.nw-fb-chip {
  display: inline-flex; align-items: center; font-size: 10.5px; font-weight: 600;
  letter-spacing: 0.02em; padding: 2px 8px; border-radius: 999px; white-space: nowrap;
  border: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12));
  color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7));
  background: var(--nw-bg-subtle, rgba(255, 255, 255, 0.04));
}
.nw-fb-chip-ok { color: var(--fb-green); border-color: var(--fb-ok-line); background: var(--fb-ok-bg); }
.nw-fb-chip-warn { color: var(--fb-amber); border-color: var(--fb-amber-line); background: var(--fb-amber-bg); }
.nw-fb-chip-strong { color: var(--fb-red); border-color: var(--fb-warn-line); background: var(--fb-warn-bg); }
.nw-fb-chip-muted { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }

/* bars: name · bar · figure · trailing columns */
.nw-fb-bars {
  display: grid; align-items: center; gap: 5px 12px;
  grid-template-columns: minmax(110px, 200px) minmax(0, 1fr) 62px repeat(var(--fb-trailing, 0), max-content);
}
.nw-fb-bars-head, .nw-fb-bar-row { display: contents; }
.nw-fb-bars-head span {
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); padding-bottom: 4px;
  border-bottom: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12)); white-space: nowrap;
}
.nw-fb-bar-name { font-size: 12.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.nw-fb-row-muted .nw-fb-bar-name { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-weight: 500; }
.nw-fb-bar { position: relative; height: 8px; border-radius: 4px; background: var(--fb-track); display: block; }
.nw-fb-bar i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 4px; background: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-bar-warn i { background: var(--fb-amber); }
.nw-fb-bar-strong i { background: var(--fb-red); }
.nw-fb-bar b { position: absolute; top: -3px; bottom: -3px; width: 1.5px; margin-left: -1px; background: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); opacity: 0.8; }
.nw-fb-bar-pct { text-align: right; font-size: 11.5px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
.nw-fb-bar-pct-warn { color: var(--fb-amber); }
.nw-fb-bar-pct-strong { color: var(--fb-red); }
.nw-fb-bar-trail { font-size: 11.5px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); white-space: nowrap; font-variant-numeric: tabular-nums; }

/* tiles: two-line rows */
.nw-fb-tiles { display: flex; flex-direction: column; }
.nw-fb-tile { padding: 8px 0; border-top: 1px solid var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-tile:first-child { border-top: 0; padding-top: 2px; }
.nw-fb-tile-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-width: 0; }
.nw-fb-tile-name { font-size: 12.5px; font-weight: 600; color: var(--nw-text, #e8edf2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nw-fb-tile-warn .nw-fb-tile-name { color: var(--fb-amber); }
.nw-fb-tile-strong .nw-fb-tile-name { color: var(--fb-red); }
.nw-fb-tile-muted .nw-fb-tile-name, .nw-fb-tile-muted .nw-fb-pair-v, .nw-fb-tile-muted .nw-fb-meter em { color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-tile-sub { font-size: 11.5px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); white-space: nowrap; }
.nw-fb-tile-head .nw-fb-chip { margin-left: auto; }
.nw-fb-tile-pairs { display: flex; flex-wrap: wrap; gap: 4px 18px; margin-top: 5px; }
.nw-fb-pair { display: inline-flex; align-items: center; gap: 7px; min-width: 0; }
.nw-fb-pair-l { font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); white-space: nowrap; }
.nw-fb-pair-v { font-size: 11.5px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }

/* runway strip: drives by time to full */
.nw-fb-runway { position: relative; height: 66px; margin: 2px 4px 10px; }
.nw-fb-runway::before { content: ""; position: absolute; left: 0; right: 0; top: 36px; height: 4px; border-radius: 2px; background: var(--fb-track); }
.nw-fb-rw-tick { position: absolute; top: 33px; width: 1px; height: 10px; background: var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-rw-t { position: absolute; top: 50px; transform: translateX(-50%); font-size: 10.5px; font-weight: 600; letter-spacing: 0.05em; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); white-space: nowrap; }
.nw-fb-rw-t.first { transform: none; }
.nw-fb-rw-t.last { transform: translateX(-100%); }
.nw-fb-rw-d { position: absolute; top: 12px; transform: translateX(-50%); display: flex; flex-direction: column; align-items: center; gap: 3px; }
.nw-fb-rw-d i { order: 2; width: 10px; height: 10px; border-radius: 50%; background: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); box-shadow: 0 0 0 2px var(--nw-bg, #141b25); margin-top: 9px; }
.nw-fb-rw-d em { order: 1; font-size: 10.5px; font-weight: 700; font-style: normal; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); white-space: nowrap; }
.nw-fb-rw-d.row2 { top: 0; }
.nw-fb-rw-d.row2 i { margin-top: 21px; }
.nw-fb-rw-d.left, .nw-fb-rw-stable { transform: translateX(-100%); align-items: flex-end; }
.nw-fb-rw-stable i { margin-right: -5px; }
.nw-fb-rw-strong i { background: var(--fb-red); }
.nw-fb-rw-strong em { color: var(--fb-red); }
.nw-fb-rw-warn i { background: var(--fb-amber); }
.nw-fb-rw-warn em { color: var(--fb-amber); }
.nw-fb-rw-stable em { max-width: 220px; overflow: hidden; text-overflow: ellipsis; }

/* score: readout on a banded track + one segmented bar */
.nw-fb-score { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin: 2px 0 10px; }
.nw-fb-score-n { font-size: 28px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; color: var(--nw-text, #e8edf2); }
.nw-fb-score-n small { font-size: 13px; font-weight: 600; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); margin-left: 2px; }
.nw-fb-score-track { position: relative; flex: 1; min-width: 160px; height: 8px; border-radius: 4px; background: var(--fb-track); margin-bottom: 12px; }
.nw-fb-score-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 4px; display: block; }
.nw-fb-score-ok { background: var(--fb-green); }
.nw-fb-score-warn { background: var(--fb-amber); }
.nw-fb-score-strong { background: var(--fb-red); }
.nw-fb-score-band { position: absolute; top: -3px; bottom: -3px; width: 1.5px; margin-left: -1px; background: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); opacity: 0.7; }
.nw-fb-score-bl { position: absolute; top: 12px; transform: translateX(-50%); font-size: 9.5px; font-weight: 600; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-variant-numeric: tabular-nums; }
.nw-fb-sevbar { display: flex; height: 10px; border-radius: 5px; overflow: hidden; gap: 2px; margin: 4px 0 6px; }
.nw-fb-sevbar i { display: block; }
.nw-fb-seg-strong { background: var(--fb-red); }
.nw-fb-seg-warn { background: var(--fb-orange); }
.nw-fb-seg-none { background: var(--fb-yellow); }
.nw-fb-seg-muted { background: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-sevkey { display: flex; flex-wrap: wrap; gap: 4px 16px; font-size: 11.5px; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); }
.nw-fb-sevkey span { display: inline-flex; align-items: center; gap: 6px; }
.nw-fb-sevkey i { width: 10px; height: 10px; border-radius: 2px; flex: none; }
.nw-fb-sevkey b { color: var(--nw-text, #e8edf2); font-variant-numeric: tabular-nums; }

@container (max-width: 520px) {
  .nw-fb-bars { grid-template-columns: minmax(90px, 140px) minmax(0, 1fr) 56px; }
  .nw-fb-bar-trail, .nw-fb-bars-head span:nth-child(n + 4) { display: none; }
  .nw-fb-rw-stable em { max-width: 120px; }
}
@container (max-width: 400px) {
  .nw-fb-tile-head .nw-fb-chip { margin-left: 0; }
  .nw-fb-tile-pairs { gap: 4px 12px; }
  .nw-fb-score-n { font-size: 24px; }
}

/* Narrow widget panel: tighter columns, then caption drops under the lane. */
@container (max-width: 520px) {
  .nw-fb-lanes { --fb-lane-day-w: 44px; --fb-lane-cap-w: 100px; column-gap: 8px; }
  .nw-fb-lanes-t.mid { display: none; }
  .nw-fb-lane-cap { font-size: 11px; }
  /* Keep tooltips clear of the card edges the narrow lane sits close to. */
  .nw-fb-lane-mark[data-tip]::after { max-width: 190px; }
}
@container (max-width: 400px) {
  .nw-fb-lanes { --fb-lane-day-w: 70px; grid-template-columns: var(--fb-lane-day-w) minmax(0, 1fr); }
  .nw-fb-lanes-head { display: none; }
  .nw-fb-lane-day { grid-column: 1; }
  .nw-fb-lane { grid-column: 2; }
  .nw-fb-lane-cap {
    grid-column: 1 / -1; height: auto; justify-content: flex-start;
    padding: 0 0 6px; margin-top: -2px; font-size: 11px;
  }
  .nw-fb-lane-cap-spike::before {
    content: "in the window"; font-size: 9.5px; font-weight: 600;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); margin-right: 4px;
  }
}


/* --- pattern heat grid (v2 chronic / plateau) -------------------------------
   24 hourly cells per lane tinted by the hour's peak over the line: one hue,
   three steps (magnitude), neutral under the line, hollow for no data. */
.nw-fb {
  --fb-over-1: rgba(217, 96, 92, 0.30);
  --fb-over-2: rgba(217, 96, 92, 0.58);
  --fb-over-3: rgba(217, 96, 92, 0.90);
  --fb-under: rgba(255, 255, 255, 0.09);
}
.nw-theme-light .nw-fb {
  --fb-over-1: rgba(179, 55, 47, 0.22);
  --fb-over-2: rgba(179, 55, 47, 0.50);
  --fb-over-3: rgba(179, 55, 47, 0.86);
  --fb-under: rgba(17, 24, 39, 0.06);
}
.nw-fb-heat { --fb-lane-cap-w: 150px; }
.nw-fb-heat-lane::before { display: none; }
.nw-fb-heat-cells {
  position: absolute; left: 0; right: 0; top: 4px; height: 20px;
  display: grid; grid-template-columns: repeat(24, minmax(0, 1fr)); gap: 2px;
}
.nw-fb-heat-cell { display: block; border-radius: 3px; background: var(--fb-under); }
.nw-fb-heat-cell.o1 { background: var(--fb-over-1); }
.nw-fb-heat-cell.o2 { background: var(--fb-over-2); }
.nw-fb-heat-cell.o3 { background: var(--fb-over-3); }
.nw-fb-heat-cell.nd { background: transparent; box-shadow: inset 0 0 0 1px var(--nw-border, rgba(255, 255, 255, 0.12)); }
.nw-fb-heat-cell:hover { box-shadow: inset 0 0 0 1.5px var(--nw-text, #e8edf2); }
.nw-fb-heat-key { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; margin-left: auto; }
.nw-fb-heat-kp { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
.nw-fb-heat-kp i { width: 14px; height: 10px; border-radius: 2px; }
.nw-fb-heat-kp em { font-style: normal; font-size: 11px; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); font-variant-numeric: tabular-nums; }

/* The week's hourly peaks against the line — headroom, not 0–100. */
.nw-fb-heat-range {
  display: grid; grid-template-columns: var(--fb-lane-day-w, 56px) minmax(0, 1fr) var(--fb-lane-cap-w, 150px);
  column-gap: 10px; align-items: center; margin-top: 12px; padding: 12px 0 14px;
}
.nw-fb-heat-range-l { font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--nw-text-tertiary, rgba(255, 255, 255, 0.5)); }
.nw-fb-heat-track { position: relative; height: 18px; }
.nw-fb-heat-track::before { content: ""; position: absolute; left: 0; right: 0; top: 7px; height: 4px; border-radius: 2px; background: var(--fb-under); }
.nw-fb-heat-band { position: absolute; top: 5px; height: 8px; border-radius: 3px; background: var(--fb-over-2); box-shadow: 0 0 0 2px var(--nw-bg, #141b25); }
.nw-fb-heat-thr { position: absolute; top: 1px; width: 2px; height: 16px; border-radius: 1px; background: var(--nw-text, #e8edf2); }
.nw-fb-heat-thr-l { position: absolute; top: -13px; transform: translateX(-50%); font-size: 10px; font-weight: 600; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); white-space: nowrap; font-variant-numeric: tabular-nums; }
.nw-fb-heat-tick { position: absolute; bottom: -13px; transform: translateX(-50%); font-size: 10.5px; font-weight: 600; color: var(--nw-text-secondary, rgba(255, 255, 255, 0.7)); white-space: nowrap; font-variant-numeric: tabular-nums; }
.nw-fb-heat-tick.first { transform: none; }
.nw-fb-heat-tick.last { transform: translateX(-100%); }
.nw-fb-heat-range-v { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; font-size: 11.5px; font-weight: 600; color: var(--fb-red); text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; line-height: 1.2; }
.nw-fb-heat-range-v .nw-fb-lane-dim { font-size: 11px; }

@container (max-width: 520px) {
  .nw-fb-heat { --fb-lane-cap-w: 104px; }
  .nw-fb-heat-cells { gap: 1px; }
  .nw-fb-heat-cell { border-radius: 2px; }
  .nw-fb-heat-range { grid-template-columns: 44px minmax(0, 1fr) 104px; column-gap: 8px; }
}
@container (max-width: 400px) {
  .nw-fb-heat .nw-fb-lane-cap-spike::before { content: none; }
  .nw-fb-heat-range { grid-template-columns: 70px minmax(0, 1fr); }
  .nw-fb-heat-range-v { grid-column: 1 / -1; align-items: flex-start; text-align: left; margin-top: 18px; }
  .nw-fb-heat-key { margin-left: 0; }
}
